// Cosmetics Store card (2026-09-28) — ONE card shape for the store catalog (catalog-list `cosmetics` render mode,
// cosmetics.php) and the product page's "More …" row (cosmetics bundle, cosmetic.php). A card is a MODEL family:
// colour skins named "<Model> — <Finish>" share it and switch via swatches.
//
// Data comes from ft_mesh_skin_store_products() (includes/functions/db/mesh_skins/mesh_skin_store_inc.php).

export interface CosmeticProduct {
	id: string;
	name: string;
	family: string;
	finish: string;
	description: string;
	priceCents: number;
	currency: string;
	category: 'armor' | 'weapon' | 'other';
	group: string;
	thumbUrl: string | null;
	/** Signed-in viewers only — a signed-out viewer gets null (and no `bodies`) and sees the thumbnail. */
	model: { url: string; sha256: string } | null;
	owned: boolean;
	canUse: boolean;
	purchasable: boolean;
	inCart: boolean;
	/** Worn looks only: one approved standard-body fitting per body profile; `model` is the first. */
	bodies: Array<{ body: string; label: string; url: string; sha256: string }>;
}

export interface CosmeticFamily {
	key: string;
	family: string;
	group: string;
	category: CosmeticProduct['category'];
	finishes: CosmeticProduct[];
}

export const COSMETIC_CATEGORY_LABELS: Record<CosmeticProduct['category'], string> = {
	armor: 'Armor Sets',
	weapon: 'Weapons',
	other: 'Other',
};

export const escapeHtml = (value: unknown): string => String(value ?? '').replace(/[&<>"']/g, (c) => (
	{ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
));

export const formatPrice = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

export const cosmeticHref = (detailUrl: string, id: string): string => `${detailUrl}?id=${encodeURIComponent(id)}`;

/** Group products into model families, preserving first-seen order. */
export function cosmeticFamilies(products: CosmeticProduct[]): CosmeticFamily[] {
	const map = new Map<string, CosmeticFamily>();
	for (const p of products) {
		const key = `${p.category}|${p.group}|${p.family}`;
		let fam = map.get(key);
		if (!fam) {
			fam = { key, family: p.family, group: p.group, category: p.category, finishes: [] };
			map.set(key, fam);
		}
		fam.finishes.push(p);
	}
	return [...map.values()];
}

export function cosmeticStateBadge(p: CosmeticProduct): string {
	if (p.owned) return '<span class="ft-cosmetic-badge ft-cosmetic-badge--owned">Owned</span>';
	if (p.canUse) return '<span class="ft-cosmetic-badge ft-cosmetic-badge--owned">Unlocked</span>';
	if (p.inCart) return '<span class="ft-cosmetic-badge ft-cosmetic-badge--cart">In cart</span>';
	return '';
}

export function cosmeticFamilyCardHtml(detailUrl: string, fam: CosmeticFamily): string {
	const first = fam.finishes[0]!; // cosmeticFamilies() never builds an empty family
	const esc = escapeHtml;
	const swatches = fam.finishes.length > 1
		? `<div class="ft-cosmetic-card__swatches" role="group" aria-label="Finishes">${fam.finishes.map((p, i) => `
			<button type="button" class="ft-cosmetic-swatch${i === 0 ? ' is-active' : ''}" data-ft-finish="${esc(p.id)}"
				title="${esc(p.finish || p.name)}" aria-label="${esc(p.finish || p.name)}" aria-pressed="${i === 0}">
				${p.thumbUrl ? `<img src="${esc(p.thumbUrl)}" alt="" loading="lazy">` : ''}
			</button>`).join('')}</div>`
		: '';
	const lowest = Math.min(...fam.finishes.map((p) => p.priceCents));
	const priceLabel = fam.finishes.every((p) => p.priceCents === lowest) ? formatPrice(first.priceCents) : `From ${formatPrice(lowest)}`;
	return `
	<article class="ft-cosmetic-card" data-ft-family="${esc(fam.key)}">
		<a class="ft-cosmetic-card__media" href="${esc(cosmeticHref(detailUrl, first.id))}" data-ft-card-link>
			<img src="${esc(first.thumbUrl || '')}" alt="${esc(first.name)}" loading="lazy" data-ft-card-img${first.thumbUrl ? '' : ' hidden'}>
			<span class="ft-cosmetic-card__noimg" data-ft-card-noimg${first.thumbUrl ? ' hidden' : ''}>No preview</span>
			<span class="ft-cosmetic-card__view">View in 3D</span>
			<span class="ft-cosmetic-card__badge" data-ft-card-badge>${cosmeticStateBadge(first)}</span>
		</a>
		<div class="ft-cosmetic-card__body">
			<a class="ft-cosmetic-card__title" href="${esc(cosmeticHref(detailUrl, first.id))}" data-ft-card-link>${esc(fam.family)}</a>
			<div class="ft-cosmetic-card__meta">
				<span class="ft-cosmetic-card__finish" data-ft-card-finish>${esc(fam.finishes.length > 1 ? first.finish : fam.group)}</span>
				<span class="ft-cosmetic-card__price">${esc(priceLabel)}</span>
			</div>
			${swatches}
		</div>
	</article>`;
}

/** One delegated listener: a swatch click re-points its card (image, links, finish label, badge) at that finish. */
export function wireCosmeticSwatches(container: HTMLElement, products: CosmeticProduct[], detailUrl: string): void {
	const byId = new Map(products.map((p) => [p.id, p]));
	container.addEventListener('click', (event) => {
		const swatch = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-ft-finish]');
		const card = swatch?.closest<HTMLElement>('[data-ft-family]');
		const p = swatch ? byId.get(swatch.dataset.ftFinish || '') : undefined;
		if (!swatch || !card || !p) return;
		card.querySelectorAll('[data-ft-finish]').forEach((s) => {
			s.classList.toggle('is-active', s === swatch);
			s.setAttribute('aria-pressed', String(s === swatch));
		});
		// Both directions: a finish with no thumbnail shows the placeholder, never the previous finish's image.
		const img = card.querySelector<HTMLImageElement>('[data-ft-card-img]');
		const noImg = card.querySelector<HTMLElement>('[data-ft-card-noimg]');
		if (img) {
			if (p.thumbUrl) img.src = p.thumbUrl;
			img.alt = p.name;
			img.hidden = !p.thumbUrl;
		}
		if (noImg) noImg.hidden = Boolean(p.thumbUrl);
		card.querySelectorAll<HTMLAnchorElement>('[data-ft-card-link]').forEach((a) => { a.href = cosmeticHref(detailUrl, p.id); });
		const finish = card.querySelector('[data-ft-card-finish]');
		if (finish) finish.textContent = p.finish;
		const badge = card.querySelector('[data-ft-card-badge]');
		if (badge) badge.innerHTML = cosmeticStateBadge(p);
	});
}
